// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, DialogTitle, DialogContent, DialogContentText, DialogActions, Dialog, CircularProgress, Portal } from '@material-ui/core';
import { DropzoneArea } from 'material-ui-dropzone';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

// Internal Dependencies
import SortableVideo from '../../global/SortableVideo/SortableVideo';
import OverLoading from '../../global/OverLoading';
import Constants from '../../../common/constants';
import { IsEmpty } from '../../../common/checks';
import VideoCropperModal from '../../global/VideoCropperModal';
import apiService from '../../../services.js/apiService';

const maxFileSize = 10485760;
const videoFormats = ['video/mp4'];

const useStyles = makeStyles(() => ({
  myDropZone: {
    position:        'relative',
    width:           '100%',
    height:          '10%',
    minHeight:       '50px',
    backgroundColor: '#ffffff',
    border:          0,
    boxShadow:       '0px 0px 1px 0px rgba(0, 0, 0, 1)',
    borderRadius:    5,
    borderColor:     '#000000',
    cursor:          'pointer',
    boxSizing:       'border-box',
  },
  loadingContainer: {
    width:           '100%',
    maxWidth:        '330px',
    margin:          'auto',
    height:          '500px',
    backgroundColor: '#BBBBBB',
    display:         'grid',
    placeItems:      'center',
    borderRadius:    '8px',
    marginTop:       '20px',
    marginBottom:    '0px !important',
  },
  loadingTextContainer: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    justifyItems:  'center',
  },
  loadingText: {
    fontSize:   '14px',
    fontWeight: 400,
    textAlign:  'center',
  },
  videoDropzoneContainer: {
    position: 'relative',
    maxWidth: '330px',
    // borderRadius: '8px',
  },
  videoUploadingOverlay: {
    width:           '330px',
    height:          '150px',
    top:             0,
    left:            0,
    borderRadius:    '5px',
    backgroundColor: '#BBBBBB',
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    rowGap:          '24px',
    justifyContent:  'center',
  },
  circularProgress: {
    color: '#007BFF',
  },

}));

const Media = (props) => {
  const { pageId } = useParams();
  // const [loaded, setLoaded] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openVideoCropperModal, setOpenVideoCropperModal] = useState(false);
  const [videoUrls, setVideoUrls] = useState([]);
  const classes = useStyles();

  useEffect(() => {
    if (!IsEmpty(props.videos)) {
      setVideoUrls(props.videos);
    }
  }, [props.videos]);

  const saveVideo = async (file) => {
    try {
      if (file) {
        setIsUploading(true);
        // To prevent an error caused by filename that has multiple dot <.> or something character else, we need to rename filename
        file = new File([file], 'o.mp4', { type: 'video/mp4' });

        const formData = new FormData();
        formData.append('video', file);
        formData.append('type', 'videos');
        formData.append('pageId', pageId);
        const cloudfrontLink = await apiService.uploadPostMedia(props.postId, formData, file);
        if (cloudfrontLink && cloudfrontLink.length > 0) {
          // setVideoUrls(videoUrls.concat(cloudfrontLink));
          // onVideoChanged(videoUrls.concat(cloudfrontLink));
          setSelectedVideo(cloudfrontLink);
          setOpenVideoCropperModal(true);
          // toast.success('Video successfully uploaded.');
        }
      }
    } catch (err) {
      toast.error(err.message);
      setIsUploading(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmation = (url) => {
    setSelectedVideo(url);
    setOpenModal(true);
  };

  const handleDeleteVideo = async () => {
    try {
      setLoading(true);

      const params = {
        id:        props.postId,
        pageId,
        mediaData: selectedVideo,
        fileType:  Constants.FileType.Video,
      };
      await apiService.deletePostMedia(props.postId, params);
      const updatedVideos = videoUrls.filter((video) => video !== selectedVideo);
      setVideoUrls(updatedVideos);
      props.onVideoChanged(updatedVideos);
      toast.success('Video successfully deleted.');
      setSelectedVideo(null);
      setOpenModal(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleErrorMessage = (file) => {
    if (!videoFormats.includes(file.type)) {
      toast.error('Only MP4 files are supported.');
      return;
    }
    if (file.size > maxFileSize) {
      toast.error('Maximum file size of 10MB exceeded.');
    }
  };

  return (
    <div className="row" style={{}}>
      {loading && <Portal><OverLoading /></Portal>}

      <div className="col-sm-12">
        <h3 className="d-block mb-5">Upload/Manage Media</h3>
        <h5>Videos</h5>
        <center>
          {
            isUploading ? (
              <div className={classes.videoUploadingOverlay}>
                <CircularProgress
                  classes={{
                    colorPrimary: classes.circularProgress,
                  }}
                />
                <div className={classes.loadingTextContainer}>
                  <span className={classes.loadingText}>Video uploading in-progress.</span>
                  <span className={classes.loadingText}>Please wait...</span>
                </div>
              </div>
            ) : (
              <div className={classes.videoDropzoneContainer}>
                <DropzoneArea
                  dropzoneText="Click to upload video"
                  onChange={(files) => {
                    if (files && files.length > 0) {
                  saveVideo(files[0]);
                    }
                  }}
                  dropzoneClass={classes.myDropZone}
                  showPreviewsInDropzone={false}
                  showPreviews={false}
                  showAlerts={false}
                  getDropRejectMessage={(file) => handleErrorMessage(file)}
                  filesLimit={1}
                  maxFileSize={maxFileSize}
                  acceptedFiles={videoFormats}
                />
              </div>
            )
          }
        </center>
        <SortableVideo
          videos={videoUrls || []}
          onSort={(d) => props.onVideoSort(d)}
          onDelete={handleConfirmation}
          postId={props.postId}
        />
      </div>
      <Dialog
        fullWidth
        size="sm"
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Remove Video</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the video?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} color="primary">Cancel</Button>
          <Button onClick={() => handleDeleteVideo()} color="primary" disabled={loading}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <VideoCropperModal
        showVideo={openVideoCropperModal}
        handleShowVideo={(state) => {
          setOpenVideoCropperModal(state);
        }}
        postId={props.postId}
        videoUrl={selectedVideo || ''}
        onCropSuccess={(video) => {
          setVideoUrls(videoUrls.concat(video));
          props.onVideoChanged(videoUrls.concat(video));
          toast.success('Video successfully uploaded.');
        }}
      />
    </div>
  );
};

Media.propTypes = {
  postId:         PropTypes.number.isRequired,
  videos:         PropTypes.arrayOf(PropTypes.string).isRequired,
  onVideoSort:    PropTypes.func.isRequired,
  onVideoChanged: PropTypes.func.isRequired,
};

export default connect(null, { })(Media);
